import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Maximize2, Minus, Plus } from 'lucide-react';
import { api, authedFetch } from '../lib/api.js';
import { standardFontDataUrl } from '../lib/pdfFonts.js';
import { cn } from '../lib/cn.js';
import { usePinchZoom } from '../lib/usePinchZoom.js';

/**
 * INV-006 — the document, drawn by us.
 *
 * "View" used to open a card of fields about a file the user never saw: the
 * supplier, the number, the total, and a download button. For a scanned
 * invoice that is backwards — the reason to open one is to read what the PDF
 * says.
 *
 * The first attempt framed the file and let the browser's own viewer paint it.
 * That works on a desktop and nowhere else: mobile browsers ship no inline PDF
 * viewer for a frame. Android Chrome offers the file as a download instead of
 * rendering it, and Safari paints a dead first page that does not scroll. Even
 * on a desktop the viewer's controls live in a toolbar that only appears on
 * hover, which a touch screen never produces. So the frame is gone and the
 * pages are rendered here, onto canvases, by pdf.js — the same engine Firefox
 * ships as its viewer.
 *
 * What that buys is that everything below is ordinary DOM: zoom is a re-render
 * at a larger scale (sharp, not an upscaled bitmap), panning is the scroll box
 * every touch screen already knows how to drag, and none of it depends on a
 * plugin the phone may not have.
 *
 * The bytes still arrive by `fetch` with the session cookie: `/api/**` answers
 * `X-Frame-Options: DENY` and `frame-ancestors 'none'` deliberately, and
 * nothing here needed that relaxed.
 *
 * pdf.js is imported only when a document is actually opened, so the ~400KB it
 * weighs never lands on anyone who does not look at an invoice.
 */

/** Zoom steps, as a multiple of the width that fits the box. */
const STEPS = [1, 1.5, 2, 3, 4];
const FIT = 1;
const MAX = 4;

/** Pages beyond this are not drawn: a long PDF would otherwise pin the tab. */
const MAX_PAGES = 20;

interface PdfPreviewProps {
  /** Same-origin URL returning `application/pdf`. Sent with credentials. */
  src: string;
  /** API path used when "full screen" / fallback needs to save the file. */
  fallbackPath: string;
  fallbackName: string;
  className?: string;
  expanded?: boolean;
}

export function PdfPreview({ src, fallbackPath, fallbackName, className, expanded=false }: PdfPreviewProps) {
  const { t } = useTranslation();
  const boxRef = useRef<HTMLDivElement>(null);
  /** The canvases' own wrapper, so a pinch can scale them without the scroll box. */
  const pagesRef = useRef<HTMLDivElement>(null);
  const [pages, setPages] = useState<HTMLCanvasElement[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [zoom, setZoom] = useState<number>(FIT);
  const [fittedWidth, setFittedWidth] = useState(0);

  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const measure = () => {
      const style = getComputedStyle(box);
      const padding = parseFloat(style.paddingInlineStart) + parseFloat(style.paddingInlineEnd);
      setFittedWidth(Math.max(box.clientWidth - padding, 120));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => observer.disconnect();
  }, []);

  /*
   * INV — pinching the document, read by the hook the signature placer shares
   * (1.18). The two buttons below are the whole zoom on a desktop; on a phone
   * the pinch is the zoom, and when the fingers lift the nearest step is
   * committed and pdf.js redraws the canvases at that scale, so what is left on
   * screen is sharp rather than an upscaled bitmap.
   */
  const pinch = usePinchZoom({
    zoom,
    min: FIT,
    max: MAX,
    steps: STEPS,
    target: pagesRef,
    onEnd: (next) => {
      if (next !== zoom) setZoom(next);
    },
  });

  useEffect(() => {
    let alive = true;
    setPages(null);
    setFailed(false);

    if (!fittedWidth) return;

    const draw = async () => {
      const [{ getDocument, GlobalWorkerOptions }, { default: PdfWorker }] = await Promise.all([
        import('pdfjs-dist/legacy/build/pdf.mjs'),
        // `?worker` rather than `?url`: pdf.js ships the worker as an ES
        // module, and handed a plain URL it starts a classic worker that never
        // answers — the document promise then hangs forever with nothing on the
        // console. Vite's `?worker` builds it properly and serves it from this
        // origin, which `default-src 'self'` already covers.
        import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?worker'),
      ]);
      GlobalWorkerOptions.workerPort = new PdfWorker();

      const response = await authedFetch(src);
      if (!response.ok) throw new Error(String(response.status));
      const bytes = new Uint8Array(await response.arrayBuffer());
      const document_ = await getDocument({
        data: bytes,
        // Only fetched when the file names a font it did not embed; see the
        // plugin in vite.config.ts.
        standardFontDataUrl,
      }).promise;

      // The box is the width to fit; without it the first render would size
      // itself to the page's own points and overflow on a phone. `clientWidth`
      // counts the padding, so a page sized from it would be wider than the
      // space it has and scroll sideways at 100%.
      const boxWidth = fittedWidth;
      // Canvases are drawn at the device's real pixel density and then sized
      // down in CSS — otherwise Hebrew at 11pt turns to mush on a phone.
      const density = Math.min(window.devicePixelRatio || 1, 2);

      const drawn: HTMLCanvasElement[] = [];
      for (let number = 1; number <= Math.min(document_.numPages, MAX_PAGES); number += 1) {
        if (!alive) return;
        const page = await document_.getPage(number);
        const unscaled = page.getViewport({ scale: 1 });
        const scale = (boxWidth / unscaled.width) * zoom;
        const viewport = page.getViewport({ scale: scale * density });

        const canvas = window.document.createElement('canvas');
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.width = `${Math.floor(viewport.width / density)}px`;
        canvas.style.height = `${Math.floor(viewport.height / density)}px`;
        canvas.className = 'block mx-auto shadow-sm rounded';

        const context = canvas.getContext('2d');
        if (!context) throw new Error('no 2d context');
        await page.render({ canvas, canvasContext: context, viewport }).promise;
        drawn.push(canvas);
      }

      if (alive) setPages(drawn);
    };

    draw().catch(() => {
      if (alive) setFailed(true);
    });

    return () => {
      alive = false;
    };
  }, [src, zoom, fittedWidth]);

  /* The canvases are real elements rather than JSX, because pdf.js paints into
     them directly — React would have nothing to render and no way to keep the
     pixels across a re-render. */
  useEffect(() => {
    const wrapper = pagesRef.current;
    if (!wrapper || !pages) return;
    wrapper.replaceChildren(...pages);
  }, [pages]);

  /*
   * 45vh on a phone, not 60.
   *
   * The box keeps its own scroll and `overscroll-contain`, so a finger that
   * lands on the document scrolls the document and never the dialog behind it
   * -- correct, and at 60vh it meant the preview owned almost the whole sheet
   * and the buttons under it could not be reached at all. The shorter box
   * leaves enough of the dialog either side of it to drag.
   */
  const frame = expanded ? 'w-full h-[76vh] sm:h-[78vh] min-h-[20rem] rounded-xl border border-border bg-muted/60' : 'w-full h-[45vh] sm:h-[52vh] min-h-[14rem] rounded-xl border border-border bg-muted/60';

  if (failed) {
    return (
      <div className={cn(frame, 'flex flex-col items-center justify-center gap-2 text-center px-4', className)}>
        <FileText className="h-6 w-6 text-muted-foreground/60" />
        <p className="text-sm text-muted-foreground">{t('common.documentFailed')}</p>
        <button
          type="button"
          onClick={() => void api.download(fallbackPath, fallbackName)}
          className="text-sm font-medium text-primary hover:underline"
        >
          {t('common.openInNewTab')}
        </button>
      </div>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      {/* `overscroll-contain` keeps a pan that reaches the edge of the last
          page from carrying on into the dialog behind it. */}
      <div
        ref={boxRef}
        /* `scrollbar-gutter: stable` so the width a page is fitted to is the
           width it still has once the vertical scrollbar arrives — otherwise
           the first render is measured 15px too wide and the box scrolls
           sideways at 100%. Phones use overlay scrollbars and never saw it. */
        className={cn(
          frame,
          'overflow-auto overscroll-contain [scrollbar-gutter:stable] p-2',
          // One finger still pans and scrolls; two are ours.
          '[touch-action:pan-x_pan-y]',
        )}
        {...pinch}
      >
        <div ref={pagesRef} className="min-h-full space-y-2 flex flex-col items-center origin-top">
          {!pages && <p className="m-auto text-sm text-muted-foreground">{t('common.loading')}</p>}
        </div>
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => setZoom((current) => STEPS[Math.max(STEPS.indexOf(current) - 1, 0)] ?? FIT)}
          disabled={zoom === FIT}
          aria-label={t('common.zoomOut')}
          className="h-10 w-10 sm:h-9 sm:w-9 rounded-lg bg-muted flex items-center justify-center hover:bg-accent transition-colors disabled:opacity-40"
        >
          <Minus className="h-4 w-4" />
        </button>
        <span className="text-xs text-muted-foreground ltr-nums w-12 text-center">{zoom * 100}%</span>
        <button
          type="button"
          onClick={() =>
            setZoom((current) => STEPS[Math.min(STEPS.indexOf(current) + 1, STEPS.length - 1)] ?? MAX)
          }
          disabled={zoom === MAX}
          aria-label={t('common.zoomIn')}
          className="h-10 w-10 sm:h-9 sm:w-9 rounded-lg bg-muted flex items-center justify-center hover:bg-accent transition-colors disabled:opacity-40"
        >
          <Plus className="h-4 w-4" />
        </button>

        {/* Still worth keeping: the browser's own viewer has search, printing
            and a page count, and on a phone it is where the system pinch
            gesture applies. */}
        <button
          type="button"
          onClick={() => void api.download(fallbackPath, fallbackName)}
          className="ms-auto h-10 sm:h-9 px-3 rounded-lg bg-muted text-xs font-medium flex items-center gap-1.5 hover:bg-accent transition-colors"
        >
          <Maximize2 className="h-3.5 w-3.5" />
          {t('common.openFullScreen')}
        </button>
      </div>
    </div>
  );
}
